// User roles for staff
export enum StaffRole {
  ADMIN = 'ADMIN',
  NURSE = 'NURSE',
  DOCTOR = 'DOCTOR',
  SUPPORT = 'SUPPORT',
}

// Permission targets (what resources can be accessed)
export enum Target {
  DASHBOARD = 'dashboard',
  PATIENT = 'patient',
  DOCTOR = 'doctor',
  CONSULTATION = 'consultation',
  APPOINTMENT = 'appointment',
  LAB_RESULT = 'lab_result',
  ORDER = 'order',
  QUIZ = 'quiz',
  USER = 'user',
  AUDIT = 'audit',
  SETTINGS = 'settings',
  PRODUCT = 'product',
  CATEGORY = 'category',
  BLOG = 'blog',
}

// Permission actions (what can be done with resources)
export enum Action {
  READ = 'read',
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  REVIEW = 'review', // For lab results
  SHARE = 'share', // For sharing with patients
  INVITE = 'invite', // For inviting doctors
}

// Permission interface
export interface Permission {
  target: Target
  actions: Action[]
}

// Role hierarchy (higher = more access)
export const ROLE_HIERARCHY: Record<StaffRole, number> = {
  [StaffRole.SUPPORT]: 1,
  [StaffRole.NURSE]: 2,
  [StaffRole.DOCTOR]: 3,
  [StaffRole.ADMIN]: 4,
}

// Get permissions for a given role
export const getRolePermissions = (role: StaffRole): Permission[] => {
  const rolePermissions: Record<StaffRole, Permission[]> = {
    [StaffRole.SUPPORT]: [
      // Read-only access to most things
      { target: Target.DASHBOARD, actions: [Action.READ] },
      { target: Target.PATIENT, actions: [Action.READ] },
      { target: Target.DOCTOR, actions: [Action.READ] },
      { target: Target.APPOINTMENT, actions: [Action.READ] },
      { target: Target.LAB_RESULT, actions: [Action.READ] },
      { target: Target.ORDER, actions: [Action.READ] },
      { target: Target.QUIZ, actions: [Action.READ] },
      { target: Target.BLOG, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.SETTINGS, actions: [Action.READ, Action.UPDATE] }, // Own settings
    ],
    [StaffRole.NURSE]: [
      // Full CRUD on patients, appointments, lab results, orders
      { target: Target.DASHBOARD, actions: [Action.READ] },
      { target: Target.PATIENT, actions: [Action.READ, Action.CREATE, Action.UPDATE] },
      { target: Target.DOCTOR, actions: [Action.READ] },
      { target: Target.CONSULTATION, actions: [Action.READ, Action.UPDATE] }, // View and schedule consultations
      { target: Target.APPOINTMENT, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.LAB_RESULT, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.SHARE] },
      { target: Target.ORDER, actions: [Action.READ, Action.CREATE, Action.UPDATE] },
      { target: Target.QUIZ, actions: [Action.READ] },
      { target: Target.SETTINGS, actions: [Action.READ, Action.UPDATE] },
    ],
    [StaffRole.DOCTOR]: [
      // Access to own patients only, can review lab results
      { target: Target.DASHBOARD, actions: [Action.READ] },
      { target: Target.PATIENT, actions: [Action.READ] }, // Filtered to own patients
      { target: Target.CONSULTATION, actions: [Action.READ, Action.UPDATE] }, // Own consultations
      { target: Target.APPOINTMENT, actions: [Action.READ, Action.UPDATE] }, // Own appointments
      { target: Target.LAB_RESULT, actions: [Action.READ, Action.REVIEW, Action.SHARE] },
      { target: Target.ORDER, actions: [Action.READ] },
      { target: Target.QUIZ, actions: [Action.READ] },
      { target: Target.SETTINGS, actions: [Action.READ, Action.UPDATE] },
    ],
    [StaffRole.ADMIN]: [
      // Full access to everything
      { target: Target.DASHBOARD, actions: [Action.READ] },
      { target: Target.PATIENT, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.DOCTOR, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE, Action.INVITE] },
      { target: Target.CONSULTATION, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.APPOINTMENT, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.LAB_RESULT, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE, Action.REVIEW, Action.SHARE] },
      { target: Target.ORDER, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.QUIZ, actions: [Action.READ, Action.DELETE] },
      { target: Target.USER, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.PRODUCT, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.CATEGORY, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.BLOG, actions: [Action.READ, Action.CREATE, Action.UPDATE, Action.DELETE] },
      { target: Target.AUDIT, actions: [Action.READ] },
      { target: Target.SETTINGS, actions: [Action.READ, Action.UPDATE] },
    ],
  }

  return rolePermissions[role] || []
}

// Check if user has specific permission
export const hasPermission = (
  userPermissions: Permission[],
  target: Target,
  action: Action
): boolean => {
  return userPermissions.some(
    (perm) => perm.target === target && perm.actions.includes(action)
  )
}

// Check if role can access a specific resource/action
export const roleHasPermission = (
  role: StaffRole,
  target: Target,
  action: Action
): boolean => {
  const permissions = getRolePermissions(role)
  return hasPermission(permissions, target, action)
}

// Display names for UI (Serbian)
export const ROLE_DISPLAY_NAMES: Record<StaffRole, string> = {
  [StaffRole.ADMIN]: 'Administrator',
  [StaffRole.NURSE]: 'Medicinska sestra',
  [StaffRole.DOCTOR]: 'Lekar',
  [StaffRole.SUPPORT]: 'Podrska',
}

export const TARGET_DISPLAY_NAMES: Record<Target, string> = {
  [Target.DASHBOARD]: 'Kontrolna tabla',
  [Target.PATIENT]: 'Pacijenti',
  [Target.DOCTOR]: 'Lekari',
  [Target.CONSULTATION]: 'Konsultacije',
  [Target.APPOINTMENT]: 'Termini',
  [Target.LAB_RESULT]: 'Laboratorijski rezultati',
  [Target.ORDER]: 'Porudzbine',
  [Target.QUIZ]: 'Upitnici',
  [Target.USER]: 'Korisnici',
  [Target.AUDIT]: 'Evidencija',
  [Target.SETTINGS]: 'Podesavanja',
  [Target.PRODUCT]: 'Proizvodi',
  [Target.CATEGORY]: 'Kategorije',
  [Target.BLOG]: 'Blog',
}

export const ACTION_DISPLAY_NAMES: Record<Action, string> = {
  [Action.READ]: 'Pregled',
  [Action.CREATE]: 'Kreiranje',
  [Action.UPDATE]: 'Izmena',
  [Action.DELETE]: 'Brisanje',
  [Action.REVIEW]: 'Pregled rezultata',
  [Action.SHARE]: 'Deljenje',
  [Action.INVITE]: 'Pozivanje',
}

// Map nav items to targets for permission checking
export const NAV_PERMISSIONS: Record<string, { target: Target; action: Action }> = {
  dashboard: { target: Target.DASHBOARD, action: Action.READ },
  patients: { target: Target.PATIENT, action: Action.READ },
  doctors: { target: Target.DOCTOR, action: Action.READ },
  consultations: { target: Target.CONSULTATION, action: Action.READ },
  appointments: { target: Target.APPOINTMENT, action: Action.READ },
  'lab-results': { target: Target.LAB_RESULT, action: Action.READ },
  orders: { target: Target.ORDER, action: Action.READ },
  'quiz-submissions': { target: Target.QUIZ, action: Action.READ },
  users: { target: Target.USER, action: Action.READ },
  audit: { target: Target.AUDIT, action: Action.READ },
  settings: { target: Target.SETTINGS, action: Action.READ },
}

// Get visible nav items for a role
export const getVisibleNavItems = (role: StaffRole): string[] => {
  const permissions = getRolePermissions(role)
  return Object.entries(NAV_PERMISSIONS)
    .filter(([, perm]) => hasPermission(permissions, perm.target, perm.action))
    .map(([key]) => key)
}
